import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { BulkImport } from '@/types/bulkImport'
import { type ActiveBulkImport, toActiveBulkImport, isActiveTerminal, isPollTerminal } from '@/types/activeBulkImport'

export const useActiveBulkImportsStore = defineStore('corp-active-bulk-imports', () => {
  const imports = ref<ActiveBulkImport[]>([])

  const byId = (id: string) => imports.value.find((i) => i.id === id) ?? null
  const live = computed(() => imports.value.filter((i) => !i.dismissed && !isActiveTerminal(i.status)))
  const pollable = computed(() =>
    imports.value.filter((i) => !isPollTerminal(i.status, i.totalCount)),
  )
  const activeCount = computed(() => live.value.length)
  const hasActive = computed(() => activeCount.value > 0)

  function track(imp: BulkImport) {
    const existing = byId(imp.id)
    if (existing) return upsertFromImport(imp)
    imports.value = [toActiveBulkImport(imp), ...imports.value]
  }

  function upsertFromImport(imp: BulkImport) {
    const idx = imports.value.findIndex((i) => i.id === imp.id)
    if (idx === -1) {
      imports.value = [toActiveBulkImport(imp), ...imports.value]
      return
    }
    const dismissed = imports.value[idx].dismissed
    imports.value[idx] = { ...toActiveBulkImport(imp), dismissed }
  }

  // Registra sem exibir toast: imports que o gestor não iniciou nesta sessão
  // (Central, reabertura) precisam de poll ao vivo, mas não de notificação.
  function trackDismissed(imp: BulkImport) {
    const existing = byId(imp.id)
    if (existing) return upsertFromImport(imp)
    imports.value = [{ ...toActiveBulkImport(imp), dismissed: true }, ...imports.value]
  }

  function update(id: string, patch: Partial<ActiveBulkImport>) {
    const idx = imports.value.findIndex((i) => i.id === id)
    if (idx !== -1) imports.value[idx] = { ...imports.value[idx], ...patch }
  }

  function refreshIfTracked(imp: BulkImport) {
    if (byId(imp.id)) upsertFromImport(imp)
  }

  function remove(id: string) {
    imports.value = imports.value.filter((i) => i.id !== id)
  }

  function dismiss(id: string) {
    // Idempotente: redispensar recriaria o objeto e realimentaria o mirror watch (loop/freeze).
    const existing = byId(id)
    if (!existing || existing.dismissed) return
    update(id, { dismissed: true })
  }

  function markPollFailed(id: string, message: string) {
    update(id, { errorMessage: message })
  }

  return {
    imports,
    live,
    pollable,
    activeCount,
    hasActive,
    byId,
    track,
    trackDismissed,
    upsertFromImport,
    refreshIfTracked,
    update,
    remove,
    dismiss,
    markPollFailed,
  }
})
